import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Card, DatePicker, Link, Message, Space, Typography, Tooltip, Grid } from "@arco-design/web-react";
import { IconCalendar, IconIdcard, IconLink, IconLocation, IconMessage } from "@arco-design/web-react/icon";
import dayjs from "dayjs";
import axios from "axios";

import { endpoints } from "../../api/endpoints";
import { trackEvent, viewedUserRole } from "../../analytics/track";
import { MeetingCard } from "../../components/MeetingCard";
import { Meeting } from "../../models/Meeting";
import { User, ProfilePreviewState } from "../../models/Person";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";
import { UserAvatar } from "../../components/UserAvatar/UserAvatar";
import { fixUrl } from "../../utils/fixUrl";
import { getButtonColor, getButtonDisabledColor, getButtonTextColor } from "../../utils/cssVariables";
import { getCurrentDate } from "../../utils/functions";
import { useTranslations } from "../../utils/hooks/useTranslations";
import { ProfileQrButton } from "../../components/ProfileQr/ProfileQrButton";
import { QR_BOOKING_SOURCE, QR_PROFILE_SOURCE, isQrProfileSource } from "../../constants/qrProfile";
import { saveAuthReturnUrl } from "../../utils/authReturnUrl";
import { loginPathWithBookMeetingIntent, registerPathWithBookMeetingIntent } from "../../constants/authBookMeeting";
import { BookMeetingAuthModal } from "../../components/BookMeetingAuthModal/BookMeetingAuthModal";
import { getProfileIndustryTags } from "../../utils/getProfileIndustryTags";
import { getLanguageSpokenLabel } from "../../utils/languageSpokenLabel";
import { ProfileTag } from "../../components/ProfileTag/ProfileTag";
import { parsePublicProfileRouteId } from "../../utils/publicProfilePath";
import { useGuestActionGuard } from "../../hooks/useGuestActionGuard";

import "../MentorProfile.css";

const Row = Grid.Row;
const Col = Grid.Col;

const show24h = !Intl.DateTimeFormat("en-US", {
  hour: "numeric",
}).resolvedOptions().hour12;

export const MentorProfileLegacy = () => {
  const { t } = useTranslation();
  const { translateSelected } = useTranslations();
  const { guardAction, handleGuestApiError } = useGuestActionGuard();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isCalendlyLinkRequired = useSelector((state: RootState) => state.config.isCalendlyLinkRequired);
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);
  const qrProfileEnabled = useSelector((state: RootState) => state.config.qrProfileEnabled);
  const brandName = useSelector((state: RootState) => state.config.brandName);

  const { id: routeId } = useParams();
  const profileUserId = parsePublicProfileRouteId(routeId);
  const [searchParams] = useSearchParams();
  const isQrProfileFlow = isQrProfileSource(searchParams.get('source'));
  const location = useLocation();
  const preview = (location.state as { profilePreview?: ProfilePreviewState } | null)?.profilePreview;
  const currentUserID = useSelector(
    (state: RootState) => state.user.user?.id
  );
  const isSurveyForm = useSelector((state: RootState) => state.config.isSurveyForm);
  
  const navigate = useNavigate();
  const [isBooking, setIsBooking] = useState(false);
  const [isConfirmBooking, setIsConfirmBooking] = useState(false);
  const [availableDayHours, setAvailableDayHours] = useState(
    {} as Record<string, { date: string; time: number }[]>
  ); // Object with available hours for each day
  const [availableDays, setAvailableDays] = useState([] as string[]); // Array of available days
  const [selectedDay, setSelectedDay] = useState(""); // Selected day
  const [selectedHour, setSelectedHour] = useState(-1); // Selected hour
  const [hours, setHours] = useState([] as number[]); // Available hours for the selected day
  const [data, setData] = useState<User>({} as User);

  const profileAttribution = useMemo(
    () => (profileUserId ? { source: QR_PROFILE_SOURCE, profileId: profileUserId } : undefined),
    [profileUserId],
  );

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [qrVisitorModalOpen, setQrVisitorModalOpen] = useState(false);

  const loadMeetings = () => {
    if (!currentUserID) {
      return;
    }
    axios
      .get(`${endpoints.meetingsWithUser}${profileUserId}?timezone=${timezone}`)
      .then((response) => {
        setMeetings(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {});
  };
;
  useEffect(() => {
    loadMeetings();
  }, [profileUserId, isBooking])

  const loadBooking = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    axios
      .get(`${endpoints.getAvailableHours}${profileUserId}?timezone=${timezone}`)
      .then((response) => {
        if (!!response.data.slots) {
          setAvailableDayHours(response.data.slots);
          setAvailableDays(Object.keys(response.data.slots));
          if (!selectedDay) {
            selectDay(getCurrentDate());
          }
        }
      });
  };

  useEffect(() => {
    loadBooking();
  }, [profileUserId, isBooking]);

  const selectDay = (day: string) => {
    const hours = availableDayHours[day];
    if (!!hours) {
      let hoursArray = [];
      for (let i = 0; i < hours.length; i++) {
        hoursArray.push(hours[i].time);
      }
      setHours(hoursArray);
    } else {
      setHours([]);
    }
    setSelectedDay(day);
  };

  useEffect(() => {
    if (!isQrProfileFlow || !currentUserID || isCalendlyLinkRequired) {
      return;
    }
    if (availableDays.length > 0) {
      setIsBooking(true);
      trackEvent("availability_view");
    }
  }, [isQrProfileFlow, currentUserID, availableDays, isCalendlyLinkRequired]);

  useEffect(() => {
    if (!isQrProfileFlow || currentUserID || !data.id) {
      return;
    }
    setQrVisitorModalOpen(true);
  }, [isQrProfileFlow, currentUserID, data.id]);

  const redirectToLoginForBooking = () => {
    saveAuthReturnUrl(`${location.pathname}${location.search}`);
    navigate(loginPathWithBookMeetingIntent(profileAttribution));
  };

  const bookMeeting = (hour: number) => {
    guardAction(() => {
      if (!selectedDay) {
        Message.error(t('notification_messages.error_no_selected_day'));
        return;
      }
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      axios
        .post(`${endpoints.bookMeeting}`, {
          mentor_id: data.id,
          date: selectedDay,
          hour: hour,
          timezone: timezone,
          ...(isQrProfileFlow ? { source: QR_BOOKING_SOURCE } : {}),
        })
        .then((response) => {
          console.log(response.data);
          Message.success({content: t('notification_messages.meeting_booked_success'), closable: true, duration: 3000});
          trackEvent("book_meeting", {
            method: "in_app",
            signup_source: isQrProfileFlow ? QR_BOOKING_SOURCE : "organic",
          });
        })
        .catch((error) => {
          if (handleGuestApiError(error)) {
            return;
          }
          console.error("Error booking meeting:", error);
          Message.error(t('notification_messages.meeting_booked_error'));
        })
        .finally(() => {
          loadBooking();
          loadMeetings();
        });
    });
  };

  const handleStartConversation = () => {
    guardAction(() => {
      navigate(routes.MESSAGES, {state: { userId: profileUserId, name: `${data.name} ${data.last_name}`, isMentor: data.isMentor}});
    });
  }


  useEffect(() => {
    setData({} as User);
    axios
      .get(`${endpoints.getPublicProfile}${profileUserId}`)
      .then((response) => {
        setData(response.data);
        if (response.data?.id) {
          trackEvent("profile_view", {
            viewed_user_role: viewedUserRole(response.data),
            signup_source: isQrProfileFlow ? QR_PROFILE_SOURCE : undefined,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching profile data:", error);
      });
  }, [profileUserId]);

  const avatarUrl = data.avatar_url ?? preview?.avatar_url;
  const firstName = data.name ?? preview?.name;
  const lastName = data.last_name ?? preview?.last_name;
  const shortDescription = data.shortDescription ?? preview?.shortDescription;
  const languageLabel = getLanguageSpokenLabel(data.language, t, useNycIntake);
  const hasProfileShell = Boolean(data.id || preview);
  const industryTags = useMemo(() => getProfileIndustryTags(data), [data]);

  const isInnerBookingAvailable = !isCalendlyLinkRequired && ((availableDays && availableDays.length > 0) || isBooking);
  const isCalendlyBookingAvailable = isCalendlyLinkRequired && !!data.calendlyLink;

  const isBookingButtonDisabled = isCalendlyLinkRequired ? !isCalendlyBookingAvailable : !isInnerBookingAvailable;

  return hasProfileShell ? (
    <>
      <BookMeetingAuthModal
        visible={qrVisitorModalOpen}
        brandName={brandName || 'WERULE'}
        mentorName={[firstName, lastName].filter(Boolean).join(' ').trim() || undefined}
        onSignIn={() => {
          saveAuthReturnUrl(`${location.pathname}${location.search}`);
          setQrVisitorModalOpen(false);
          navigate(loginPathWithBookMeetingIntent(profileAttribution));
        }}
        onRegister={() => {
          saveAuthReturnUrl(`${location.pathname}${location.search}`);
          setQrVisitorModalOpen(false);
          navigate(registerPathWithBookMeetingIntent(profileAttribution));
        }}
        onClose={() => setQrVisitorModalOpen(false)}
      />
      <Card className="card-with-icon-hover own-card mentor-profile-card">
        <div className="mentor-profile-card__body">
          <Row gutter={[24, 16]} className="mentor-profile-card__hero-row">
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={11}
              xxl={11}
              className="mentor-profile-card__avatar-col"
            >
              <div className="mentor-profile__avatar-wrap">
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    overflow: "hidden",
                    zIndex: 1,
                  }}
                >
                  <UserAvatar
                    avatarUrl={avatarUrl}
                    firstName={firstName}
                    lastName={lastName}
                    shape="square"
                    alt="avatar"
                  />
                </div>
              </div>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={13}
              xxl={13}
              className="mentor-profile-card__info-col"
            >
              <Typography.Title
                heading={1}
                className="mentor-profile-card__name"
              >
                {[firstName, lastName].filter(Boolean).join(" ")}
              </Typography.Title>
              {!!shortDescription && (
                <Typography.Title
                  heading={5}
                  className="mentor-profile-card__role"
                >
                  {shortDescription}
                </Typography.Title>
              )}
              <div className="mentor-profile__meta-list">
                {!!languageLabel && (
                  <Typography.Title heading={5} className="mentor-profile__meta-line">
                    {languageLabel}
                  </Typography.Title>
                )}
                {!!data.location && !useNycIntake && (
                  <Typography.Title heading={5} className="mentor-profile__meta-line">
                    <IconLocation /> {data.location}
                  </Typography.Title>
                )}
                <div className="mentor-profile__social-links">
                  {!!data && data.linkedIn && (
                    <Link
                      href={fixUrl(data.linkedIn)}
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                      style={{ display: "block" }}
                    >
                      <Typography.Title heading={5} className="mentor-profile__meta-line">
                        <IconIdcard /> {t('mentor_profile.linkedin')}
                      </Typography.Title>
                    </Link>
                  )}
                  {!!data && data.website && (
                    <Link
                      href={fixUrl(data.website)}
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                      style={{ display: "block" }}
                    >
                      <Typography.Title heading={5} className="mentor-profile__meta-line">
                        <IconLink /> {t('mentor_profile.website')}
                      </Typography.Title>
                    </Link>
                  )}
                  {qrProfileEnabled && !!data.id && (
                    <ProfileQrButton
                      userId={data.id}
                      isMentor={data.isMentor !== false}
                      fileLabel={`${data.id}`}
                      profileName={firstName}
                      profileLastName={lastName}
                    />
                  )}
                </div>
              </div>
              <div className="mentor-profile-card__tags">
                {!!data.categories?.length && (
                  <Typography.Paragraph style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: 0 }}>
                    {translateSelected(
                      useNycIntake ? "advisorCategories" : "categories",
                      data.categories
                    ).map((c: string) => (
                      <ProfileTag key={c} label={c} />
                    ))}
                  </Typography.Paragraph>
                )}
                {industryTags.length > 0 && (
                  <Typography.Paragraph style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: 0 }}>
                    {translateSelected("industries_nyc", industryTags).map((c: string) => (
                      <ProfileTag key={c} label={c} />
                    ))}
                  </Typography.Paragraph>
                )}
              </div>
              <div className="mentor-profile__actions">
              {!!data && (
                <div className="mentor-profile__action-slot">
                  <Tooltip
                    className="profile-booking-link-tooltip"
                    content={t('common.no_calendly_link_message')}
                    disabled={!isCalendlyLinkRequired || !!data.calendlyLink}
                  >
                    <Button
                      className="mentor-profile__action-button"
                      style={{
                        color: getButtonTextColor(),
                        backgroundColor: isBookingButtonDisabled ? getButtonDisabledColor() : getButtonColor(),
                      }}
                      onClick={() => {
                        if (!currentUserID) {
                          redirectToLoginForBooking();
                          return;
                        }
                        guardAction(() => {
                          if (isCalendlyLinkRequired) {
                            window.open(data.calendlyLink, '_blank');
                            trackEvent("book_meeting", { method: "calendly" });
                            return;
                          }
                          setIsBooking((isBooking) => {
                            const next = !isBooking;
                            if (next) {
                              trackEvent("availability_view");
                            }
                            return next;
                          });
                        });
                      }}
                      icon={<IconCalendar />}
                      size={"large"}
                      shape={"round"}
                      disabled={isCalendlyLinkRequired ? !isCalendlyBookingAvailable : !isInnerBookingAvailable}
                    >
                      {isCalendlyLinkRequired ? t('common.book_via_calendly') : !availableDays || availableDays.length === 0 ? (
                        t('mentor_profile.no_available_slots')
                      ) : (
                          t('mentor_profile.book_meeting_button')
                      )}
                    </Button>
                  </Tooltip>
                </div>
              )}
              {!!data.id && !!currentUserID && data.id !== currentUserID && (
                <div className="mentor-profile__action-slot">
                <Button
                  className="mentor-profile__action-button"
                  style={{
                    color: getButtonTextColor(),
                    backgroundColor: getButtonColor(),
                  }}
                  onClick={handleStartConversation}
                  icon={<IconMessage />}
                  size={"large"}
                  shape={"round"}
                >
                  {t('mentor_profile.send_message_button')}
                </Button>
                </div>
              )}
              </div>
            </Col>
          </Row>
        </div>
      </Card>
      {isBooking && !isConfirmBooking && (
        <Card
          className="card-with-icon-hover own-card"
          style={{
            marginTop: 20,
          }}
          title={t('mentor_profile.book_meeting_card')}
        >
          {!!availableDays && availableDays.length > 0 && (
            <>
              <Row gutter={24}>
                <Col
                  xs={24}
                  sm={24}
                  md={11}
                  lg={11}
                  xl={11}
                  xxl={11}
                  style={{ marginBottom: 20 }}
                >
                  <Typography.Paragraph bold>{t('mentor_profile.select_day_label')}</Typography.Paragraph>
                  <div
                    style={{ maxWidth: 250, width: "100%", margin: "0 auto" }}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(current) => {
                        // check in availableDays
                        return !availableDays.includes(
                          dayjs(current).format("YYYY-MM-DD")
                        );
                      }}
                      placeholder={t('mentor_profile.select_date_placeholder')}
                      onChange={(date) => {
                        selectDay(dayjs(date).format("YYYY-MM-DD"));
                      }}
                    />
                  </div>
                </Col>
                {!!selectedDay && (
                  <Col
                    xs={24}
                    sm={24}
                    md={13}
                    lg={13}
                    xl={13}
                    xxl={13}
                    style={{ marginBottom: 20 }}
                  >
                    <Typography.Paragraph bold>
                      {t('mentor_profile.available_hours', { day: selectedDay })}
                    </Typography.Paragraph>
                    <div
                      style={{ maxWidth: 250, width: "100%", margin: "0 auto" }}
                    >
                      {hours.map((hour) => (
                        <div style={{ marginBottom: 10, width: "100%" }}>
                          <Button
                            key={hour}
                            onClick={() => {
                              // bookMeeting(hour);
                              setSelectedHour(hour);
                              setIsConfirmBooking(true);
                            }}
                            style={{
                              width: "100%",
                              color: getButtonTextColor(),
                              backgroundColor: getButtonColor(),
                            }}
                            size={"large"}
                            shape={"round"}
                          >
                            {show24h ? hour : hour > 12 ? hour - 12 : hour}:00{" "}
                            {hour >= 12 ? "PM" : "AM"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Col>
                )}
              </Row>

              <div>{t('mentor_profile.timezone_message', {timezone})}</div>
            </>
          )}
          {(!availableDays || availableDays.length === 0) && (
            <Typography.Paragraph>
              {t('mentor_profile.no_available_hours')}
            </Typography.Paragraph>
          )}
        </Card>
      )}
      {isConfirmBooking && (
        <Card
          className="card-with-icon-hover own-card"
          style={{
            marginTop: 20,
          }}
          title={t('mentor_profile.confirm_booking_button')}
        >
          <Typography.Paragraph>
            {t('mentor_profile.booking_confirmation', {
              name: `${data.name} ${data.last_name}`, date: selectedDay, time: show24h
                ? selectedHour
                : selectedHour > 12
                  ? selectedHour - 12
                  : selectedHour
            })}
            {selectedHour >= 12 ? "PM" : "AM"}
          </Typography.Paragraph>
          <Button
            onClick={() => {
              setIsConfirmBooking(false);
            }}
            style={{ marginRight: 10 }}
            size={"large"}
            shape={"round"}
          >
            {t('common.cancel_button')}
          </Button>
          <Button
            onClick={() => {
              bookMeeting(selectedHour);
              setIsConfirmBooking(false);
              setIsBooking(false);
            }}
            style={{
              marginLeft: 24,
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            }}
            size={"large"}
            shape={"round"}
          >
            {t('mentor_profile.confirm_booking_button')}
          </Button>

          <div style={{ marginTop: 10 }}>
            {t('mentor_profile.timezone_message', {timezone})}
          </div>
        </Card>
      )}
      {!!meetings && meetings.length > 0 && (
        <Card
          className="card-with-icon-hover own-card"
          style={{
            marginTop: 20,
          }}
          title={ t('mentor_profile.upcoming_meetings') +
            (data.isMentor ? t('mentor_profile.mentor') : t('mentor_profile.person'))
          }
        >
          <div className="upcoming-meetings-grid">
            {meetings.map((meeting, index) => (
              <MeetingCard
                key={`meeting-${index}`}
                meeting={meeting}
                onCancelMeeting={loadMeetings}
              />
            ))}
          </div>
          <div> {t('mentor_profile.timezone_message', { timezone })}</div>
        </Card>
      )}
      {!isBooking && !!data?.fullBio && (
        <Card
          className="card-with-icon-hover own-card"
          style={{
            marginTop: 20,
            textAlign: "left",
          }}
        >
          <Typography.Title
            heading={4}
            style={{ marginTop: 0, textTransform: "uppercase" }}
          >
            {t('mentor_profile.about_me')}
          </Typography.Title>
          <Typography.Paragraph
            className="mentor-profile__about-body"
            style={{
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              margin: 0,
              padding: 0,
            }}
          >
            {data?.fullBio}
          </Typography.Paragraph>
        </Card>
      )}
    </>
  ) : (
      <div> {t('common.loading_data')}</div>
  );
};
